import {getAreaSymbol, StaticPartSymbolShape} from './symbols'

import {
  createPolygonPath,
  getViewBox,
  toViewBoxString
} from '@refra/modules/refra-static-parts/components/StaticPartReplacementEdit/Detail/DetailDrawing/helpers'
import {StaticPartsColors} from '@refra/modules/refra-static-parts/enums'
import {
  StaticMaterialInstallationDTO,
  StaticPartComponentDto,
  StaticPartVertexDto
} from '@refra/modules/refra-static-parts/types'

interface StaticPartShutdownPrintDrawingProps {
  selectedComponent: StaticPartComponentDto
  materialInstallations: StaticMaterialInstallationDTO[]
}

const STROKE_WIDTH = 0.03

const getSectionBounds = (vertices: StaticPartVertexDto[]) => {
  const xs = vertices.map((vertex) => vertex.x)
  // y is negated when rendered (see createPolygonPath)
  const ys = vertices.map((vertex) => -vertex.y)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)

  return {
    cx: (minX + maxX) / 2,
    cy: (minY + maxY) / 2,
    size: Math.min(maxX - minX, maxY - minY)
  }
}

/**
 * Static, non-interactive rendering of a static part component used in the printout.
 * Sections are filled with their area color and overlaid with the area's symbol
 * (circle, slash, plus, minus, triangle, square) for black-and-white legibility.
 */
export const StaticPartShutdownPrintDrawing = ({
  selectedComponent,
  materialInstallations
}: StaticPartShutdownPrintDrawingProps) => {
  const viewBox = toViewBoxString(getViewBox(selectedComponent))

  const areaIndexBySectionId = new Map<number, number>()
  materialInstallations.forEach((installation, areaIndex) => {
    installation.sections?.forEach((section) => {
      areaIndexBySectionId.set(section.id, areaIndex)
    })
  })

  return (
    <svg
      viewBox={viewBox}
      className="w-full"
      style={{maxHeight: '58vh'}}
      preserveAspectRatio="xMidYMid meet"
      data-cy="static_part_shutdown_print_drawing"
    >
      <g id="annotationShapes">
        {selectedComponent.annotationShapes?.map((annotationShape, index) => (
          <polygon
            key={`${selectedComponent.id}-annotationShape-${index}`}
            points={createPolygonPath(annotationShape.vertices)}
            stroke="black"
            strokeWidth={STROKE_WIDTH}
            fill="transparent"
          />
        ))}
      </g>
      <g id="annotationLabels">
        {selectedComponent.annotationLabels?.map((annotationLabel, index) => (
          <text
            key={`${selectedComponent.id}-annotationLabel-${index}`}
            x={annotationLabel.position.x}
            y={-annotationLabel.position.y}
            transform={`rotate(${annotationLabel.rotation}, ${annotationLabel.position.x}, ${-annotationLabel.position.y})`}
            fontSize={annotationLabel.height}
            fill="black"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {annotationLabel.text}
          </text>
        ))}
      </g>
      <g id="sections">
        {selectedComponent.sections.map((section) => {
          const areaIndex = areaIndexBySectionId.get(section.id)
          const fill =
            areaIndex !== undefined
              ? StaticPartsColors[areaIndex % StaticPartsColors.length]
              : 'transparent'

          return (
            <polygon
              key={`${selectedComponent.id}-polygon-${section.id}`}
              points={createPolygonPath(section.vertices)}
              stroke="black"
              strokeWidth={STROKE_WIDTH}
              fill={fill}
            />
          )
        })}
      </g>
      <g id="sectionSymbols">
        {selectedComponent.sections.map((section) => {
          const areaIndex = areaIndexBySectionId.get(section.id)
          if (areaIndex === undefined) return null

          const symbol = getAreaSymbol(areaIndex)
          if (!symbol) return null

          const {cx, cy, size} = getSectionBounds(section.vertices)
          if (size <= 0) return null

          return (
            <StaticPartSymbolShape
              key={`${selectedComponent.id}-symbol-${section.id}`}
              symbol={symbol}
              cx={cx}
              cy={cy}
              size={size * 0.5}
              strokeWidth={size * 0.06}
              color="black"
            />
          )
        })}
      </g>
    </svg>
  )
}
